import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useNearMiss, useResolveNearMiss } from '@/hooks/use-safety';
import { useEscalateNearMiss } from '@/hooks/use-compliance-incidents';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { NearMissStatus, NearMissSeverity } from '@/lib/types';

const statusColors: Record<NearMissStatus, string> = {
  reported: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  action_required: 'bg-orange-100 text-orange-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

const severityColors: Record<NearMissSeverity, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export function NearMissDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reportId = parseInt(id || '0', 10);
  const { data: report, isLoading } = useNearMiss(reportId);
  const resolveMutation = useResolveNearMiss();
  const escalateMutation = useEscalateNearMiss();

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Near-miss report not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/near-misses">Back to Near-Miss Reports</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/near-misses')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
            <p className="text-gray-600">
              {report.location} &middot; {formatDateOnly(report.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={severityColors[report.severity_potential]}>
            {report.severity_potential}
          </Badge>
          <Badge className={statusColors[report.status]}>
            {report.status.replace('_', ' ')}
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Report Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Reported By:</span>{' '}
              {report.anonymous ? 'Anonymous' : report.reported_by_username}
            </div>
            <div>
              <span className="text-gray-500">Date:</span>{' '}
              {formatDateOnly(report.date)}
            </div>
            <div>
              <span className="text-gray-500">Location:</span> {report.location}
            </div>
            <div>
              <span className="text-gray-500">Reported At:</span>{' '}
              {formatDateTimeInTimeZone(report.created_at, timeZone)}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Description</p>
            <p className="text-sm whitespace-pre-wrap">{report.description}</p>
          </div>
          {report.root_cause && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Root Cause</p>
              <p className="text-sm whitespace-pre-wrap">{report.root_cause}</p>
            </div>
          )}
          {report.corrective_actions && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Corrective Actions</p>
              <p className="text-sm whitespace-pre-wrap">{report.corrective_actions}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {report.follow_ups && report.follow_ups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Follow-Up Actions ({report.follow_ups.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.follow_ups.map((followUp) => (
              <div key={followUp.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{followUp.action}</span>
                  <Badge
                    className={
                      followUp.completed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }
                  >
                    {followUp.completed ? 'Complete' : 'Pending'}
                  </Badge>
                </div>
                <div className="text-xs text-gray-500">
                  Assigned to: {followUp.assigned_to_username || 'Unassigned'}
                  {followUp.due_date && ` \u00b7 Due: ${formatDateOnly(followUp.due_date)}`}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {report.corrective_actions && report.status === 'resolved' && (
        <Card>
          <CardHeader>
            <CardTitle>Resolution</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{report.corrective_actions}</p>
            {report.resolved_by_username && (
              <p className="text-sm text-gray-500 mt-2">
                Resolved by {report.resolved_by_username}
                {report.resolved_at && ` on ${formatDateTimeInTimeZone(report.resolved_at, timeZone)}`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() =>
            escalateMutation.mutate(
              { near_miss_id: report.id },
              {
                onSuccess: (data) => {
                  navigate(`/compliance/incidents/${data.id}`);
                },
              },
            )
          }
          disabled={escalateMutation.isPending}
        >
          {escalateMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <AlertTriangle className="mr-2 h-4 w-4" />
          )}
          Escalate to Incident
        </Button>
        {(report.status === 'reported' || report.status === 'under_review') && (
          <Button
            onClick={() =>
              resolveMutation.mutate({
                id: report.id,
                data: { root_cause: '', corrective_actions: 'Resolved' },
              })
            }
            disabled={resolveMutation.isPending}
          >
            {resolveMutation.isPending ? 'Resolving...' : 'Mark as Resolved'}
          </Button>
        )}
      </div>
    </div>
  );
}
